import type { SupportedLanguage } from "../types/domain";
import { resolveCountryForPlaceQuery } from "../components/PlacePicker";
import { travelCountryOptions } from "../profile/worldCountries";
import { COUNTRY_FACTS_DATASET_VERSION as BASE_COUNTRY_FACTS_DATASET_VERSION, MUNDELY_COUNTRY_FACTS } from "../data/countryFacts";
import { USEFUL_INFO_GLOBAL_SNAPSHOT_META } from "../data/usefulInfoGlobalSnapshot";
import { loadApprovedUsefulInfoCountry } from "../data/usefulInfoApprovedFirestore";
import type { UsefulInfoFieldId } from "./tripStore";

export const COUNTRY_FACTS_DATASET_VERSION = `${BASE_COUNTRY_FACTS_DATASET_VERSION}+global:${USEFUL_INFO_GLOBAL_SNAPSHOT_META.version}`;

export const USEFUL_INFO_NOT_REQUIRED = "__MUNDELY_NOT_REQUIRED__";

type LocalizedPresetValue = Partial<Record<SupportedLanguage, string>> | string;
type CountryPresetFacts = Partial<Record<UsefulInfoFieldId, LocalizedPresetValue>>;

function localizedPreset(it: string, en: string, es: string): Partial<Record<SupportedLanguage, string>> {
  return { it, en, es };
}

const COUNTRY_PRESET_FACTS: Record<string, CountryPresetFacts> = {
  JP: {
    drinkingWater: localizedPreset(
      "Generalmente potabile nelle città e nelle strutture principali. In zone molto rurali o se hai dubbi, meglio acqua in bottiglia.",
      "Generally safe to drink in cities and main facilities. In very rural areas or if unsure, bottled water is safer.",
      "Generalmente potable en ciudades y en la mayoría de las instalaciones. En zonas muy rurales o si tienes dudas, mejor agua embotellada."
    ),
    taxiApps: localizedPreset("GO, Uber, DiDi", "GO, Uber, DiDi", "GO, Uber, DiDi"),
    foodDeliveryApps: localizedPreset("Uber Eats, Demae-can", "Uber Eats, Demae-can", "Uber Eats, Demae-can"),
    emergencyNumber: localizedPreset(
      "Polizia: 110 · Ambulanza/Vigili del fuoco: 119",
      "Police: 110 · Ambulance/Fire: 119",
      "Policía: 110 · Ambulancia/Bomberos: 119"
    ),
    governmentType: localizedPreset("Monarchia costituzionale parlamentare", "Parliamentary constitutional monarchy", "Monarquía constitucional parlamentaria"),
    religion: localizedPreset("Shintoismo e Buddhismo sono le tradizioni principali", "Shinto and Buddhism are the main traditions", "Sintoísmo y budismo son las tradiciones principales"),
    publicAffection: localizedPreset("In genere discreto: meglio evitare effusioni molto esplicite in pubblico.", "Usually discreet: avoid very explicit displays of affection in public.", "Suele ser discreto: mejor evitar muestras de afecto muy explícitas en público."),
    lgbtq: localizedPreset("Le grandi città sono più aperte, ma è consigliata discrezione in base al contesto.", "Major cities are more open, but some discretion is still advisable depending on the context.", "Las grandes ciudades son más abiertas, pero sigue siendo aconsejable cierta discreción según el contexto."),
    specialPrecautions: localizedPreset("Trasporti molto puntuali, regole sociali rigide e contanti ancora utili in alcuni contesti.", "Transport is very punctual, social rules are quite strict, and cash is still useful in some situations.", "El transporte es muy puntual, las normas sociales son bastante estrictas y el efectivo sigue siendo útil en algunos contextos.")
  },
  DE: {
    drinkingWater: localizedPreset(
      "Generalmente potabile dal rubinetto in tutto il Paese.",
      "Tap water is generally safe across the country.",
      "El agua del grifo suele ser potable en todo el país."
    ),
    taxiApps: localizedPreset("FREENOW, Uber", "FREENOW, Uber", "FREENOW, Uber"),
    foodDeliveryApps: localizedPreset("Lieferando, Uber Eats, Wolt", "Lieferando, Uber Eats, Wolt", "Lieferando, Uber Eats, Wolt"),
    emergencyNumber: localizedPreset(
      "Emergenze generali: 112 · Polizia: 110",
      "General emergencies: 112 · Police: 110",
      "Emergencias generales: 112 · Policía: 110"
    ),
    governmentType: localizedPreset("Repubblica federale parlamentare", "Federal parliamentary republic", "República federal parlamentaria"),
    religion: localizedPreset("Cristianesimo (cattolico e protestante) con ampia secolarizzazione", "Christianity (Catholic and Protestant) with broad secularisation", "Cristianismo (católico y protestante) con amplia secularización"),
    publicAffection: localizedPreset("Generalmente accettata se contenuta e rispettosa del contesto.", "Generally accepted when moderate and respectful of the setting.", "Generalmente aceptada si es moderada y respetuosa con el contexto."),
    lgbtq: localizedPreset("In genere ben accolta, soprattutto nelle grandi città.", "Generally well accepted, especially in major cities.", "Generalmente bien aceptada, sobre todo en las grandes ciudades."),
    specialPrecautions: localizedPreset("Molti pagamenti sono digitali, ma avere una carta e un po' di contanti può comunque essere utile.", "Digital payments are common, but carrying a card and some cash can still be useful.", "Los pagos digitales son frecuentes, pero sigue siendo útil llevar tarjeta y algo de efectivo.")
  }
};

function resolvePresetValue(value: LocalizedPresetValue | undefined, language: SupportedLanguage): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[language] || value.en || value.it || value.es || "";
}


const DOMESTIC_NOT_REQUIRED_FIELDS: UsefulInfoFieldId[] = [
  "embassy",
  "consulates",
  "requiredVaccines",
  "visa",
  "passport",
  "internationalDrivingPermit"
];

export interface UsefulInfoCountryContext {
  destinationCountryCode: string;
  nationalityCode: string;
  stableValues: Partial<Record<UsefulInfoFieldId, string>>;
  /** Baseline embedded in the app; needed to preserve user-level overrides. */
  baseStableValues: Partial<Record<UsefulInfoFieldId, string>>;
  /** Only reviewed and approved values, never the pending import snapshot. */
  approvedValues: Partial<Record<UsefulInfoFieldId, string>>;
  approvedDescriptions: Partial<Record<UsefulInfoFieldId, string>>;
  approvedVersion?: number;
  approvedLanguage?: SupportedLanguage;
  domesticNotRequiredValues: Partial<Record<UsefulInfoFieldId, string>>;
}

function normalizeSearchText(value: string): string {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function normalizeNationalityCode(value: string): string {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function directCountryMatch(destination: string): string {
  const raw = String(destination || "").trim();
  const code = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(code) && MUNDELY_COUNTRY_FACTS[code]) return code;

  const normalized = normalizeSearchText(raw);
  if (!normalized) return "";
  const languages: SupportedLanguage[] = ["it", "en", "es"];
  for (const language of languages) {
    for (const option of travelCountryOptions(language)) {
      const label = normalizeSearchText(option.label);
      if (!label) continue;
      if (normalized === label || normalized.endsWith(` ${label}`) || normalized.startsWith(`${label} `)) return option.code;
    }
  }
  return "";
}



const OFFLINE_PLACE_COUNTRY_ALIASES: Array<{ countryCode: string; aliases: string[] }> = [
  { countryCode: "IT", aliases: ["roma", "lecce"] },
  {
    countryCode: "JP",
    aliases: [
      "tokyo", "kyoto", "osaka", "nara", "hiroshima", "sapporo", "fukuoka",
      "nagoya", "yokohama", "kobe", "kanazawa", "hakone", "nikko", "okinawa",
      "miyajima", "takayama"
    ]
  }
];

function offlinePlaceCountryMatch(destination: string): string {
  const normalized = normalizeSearchText(destination);
  if (!normalized) return "";
  for (const entry of OFFLINE_PLACE_COUNTRY_ALIASES) {
    for (const alias of entry.aliases) {
      const normalizedAlias = normalizeSearchText(alias);
      if (
        normalized === normalizedAlias ||
        normalized.startsWith(`${normalizedAlias} `) ||
        normalized.endsWith(` ${normalizedAlias}`)
      ) return entry.countryCode;
    }
  }
  return "";
}

function cacheKey(destination: string): string {
  return `mundely.usefulInfo.country.v2.${normalizeSearchText(destination).slice(0, 120)}`;
}

export async function resolveUsefulInfoDestinationCountryCode(destination: string, language: SupportedLanguage): Promise<string> {
  const direct = directCountryMatch(destination);
  if (direct) return direct;

  const offlinePlace = offlinePlaceCountryMatch(destination);
  if (offlinePlace) return offlinePlace;

  const key = cacheKey(destination);
  try {
    const cached = String(localStorage.getItem(key) || "").toUpperCase();
    if (/^[A-Z]{2}$/.test(cached)) return cached;
  } catch {
    // Cache is optional.
  }

  try {
    const resolved = await resolveCountryForPlaceQuery(destination, language);
    const code = String(resolved?.countryCode || "").toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return "";
    try { localStorage.setItem(key, code); } catch { /* optional cache */ }
    return code;
  } catch {
    return "";
  }
}

function stableValuesForCountry(countryCode: string, language: SupportedLanguage): Partial<Record<UsefulInfoFieldId, string>> {
  const fact = MUNDELY_COUNTRY_FACTS[countryCode];
  if (!fact) return {};
  const values: Partial<Record<UsefulInfoFieldId, string>> = {
    drivingSide: fact.drivingSide
  };
  if (fact.plugTypes.length) values.plugType = fact.plugTypes.join(", ");
  if (fact.currencies.length) values.currency = fact.currencies[0];

  const preset = COUNTRY_PRESET_FACTS[countryCode];
  if (preset) {
    for (const [field, presetValue] of Object.entries(preset) as Array<[UsefulInfoFieldId, LocalizedPresetValue]>) {
      const resolved = resolvePresetValue(presetValue, language);
      if (resolved) values[field] = resolved;
    }
  }

  return values;
}

function domesticNotRequiredValues(destinationCountryCode: string, nationalityCode: string): Partial<Record<UsefulInfoFieldId, string>> {
  if (!destinationCountryCode || !nationalityCode || destinationCountryCode !== nationalityCode) return {};
  return DOMESTIC_NOT_REQUIRED_FIELDS.reduce((result, field) => {
    result[field] = USEFUL_INFO_NOT_REQUIRED;
    return result;
  }, {} as Partial<Record<UsefulInfoFieldId, string>>);
}

export async function buildUsefulInfoCountryContext(
  destination: string,
  nationality: string,
  language: SupportedLanguage
): Promise<UsefulInfoCountryContext | null> {
  const destinationCountryCode = await resolveUsefulInfoDestinationCountryCode(destination, language);
  if (!destinationCountryCode) return null;
  const nationalityCode = normalizeNationalityCode(nationality);
  const baseStableValues = stableValuesForCountry(destinationCountryCode, language);
  const approved = await loadApprovedUsefulInfoCountry(destinationCountryCode, language);
  return {
    destinationCountryCode,
    nationalityCode,
    stableValues: { ...baseStableValues, ...(approved?.values || {}) },
    baseStableValues,
    approvedValues: approved?.values || {},
    approvedDescriptions: approved?.descriptions || {},
    approvedVersion: approved?.version,
    approvedLanguage: approved?.version ? language : undefined,
    domesticNotRequiredValues: domesticNotRequiredValues(destinationCountryCode, nationalityCode)
  };
}

export function isUsefulInfoNotRequired(value: string): boolean {
  return String(value || "").trim() === USEFUL_INFO_NOT_REQUIRED;
}
